import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CheckCircle2, Sparkles } from "lucide-react";
import { FEATURED_WORK, AE_LOGO_URL } from "@/lib/constants";

export default function WorkDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const item = FEATURED_WORK.find((w) => w.slug === slug);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        if (item) {
            document.title = `${item.title} — ${item.client} | AE Case Study`;
        } else {
            document.title = "Case study not found | AE";
        }
    }, [item]);

    if (!item) {
        return (
            <main
                data-testid="work-detail-not-found"
                className="min-h-screen w-full bg-white text-ae-ink flex items-center justify-center px-6"
            >
                <div className="max-w-md text-center">
                    <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange">
                        404
                    </div>
                    <h1 className="mt-4 font-display font-extrabold text-4xl md:text-5xl text-ae-ink">
                        Case study not found.
                    </h1>
                    <p className="mt-4 text-ae-gray-700">
                        We couldn&apos;t locate that project. Try browsing the
                        portfolio below.
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate("/#work")}
                        data-testid="work-detail-back-portfolio"
                        className="mt-8 inline-flex items-center gap-2 bg-ae-orange text-white rounded-full px-6 py-3 text-sm font-semibold hover:bg-ae-orange-light transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to Work
                    </button>
                </div>
            </main>
        );
    }

    const hasDetail = Boolean(item.objective || item.approach || item.results);

    return (
        <main
            data-testid="work-detail-root"
            className="min-h-screen w-full bg-white text-ae-ink"
        >
            {/* Minimal nav */}
            <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/5">
                <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
                    <Link
                        to="/"
                        data-testid="work-detail-logo"
                        className="flex items-center gap-3 group"
                    >
                        <img
                            src={AE_LOGO_URL}
                            alt="AE"
                            className="h-10 w-10 rounded-full object-cover"
                            style={{ mixBlendMode: "multiply" }}
                        />
                        <div className="leading-none">
                            <div className="font-display font-bold text-base text-ae-ink">
                                Amplified eSolutions
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-ae-gray-500 font-medium mt-1">
                                Case Study
                            </div>
                        </div>
                    </Link>
                    <Link
                        to="/#work"
                        data-testid="work-detail-back-link"
                        className="inline-flex items-center gap-2 text-sm font-semibold text-ae-ink hover:text-ae-orange transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" /> Back to Work
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-ae-gray-100">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="flex flex-wrap items-center gap-3"
                    >
                        <span
                            data-testid="work-detail-tag"
                            className="text-[11px] uppercase tracking-[0.22em] font-bold text-ae-orange bg-ae-orange/10 border border-ae-orange/30 px-3 py-1.5 rounded-full"
                        >
                            {item.tag}
                        </span>
                        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-ae-gray-500">
                            {item.category}
                        </span>
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 22 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.85, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="work-detail-title"
                        className="mt-5 font-display font-extrabold text-4xl md:text-6xl lg:text-7xl text-ae-ink tracking-tight leading-[1.02]"
                    >
                        {item.title}
                    </motion.h1>
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.12 }}
                        data-testid="work-detail-client"
                        className="mt-3 text-lg md:text-xl text-ae-gray-700"
                    >
                        <span className="text-ae-orange font-semibold">Client</span>{" "}
                        &middot; {item.client}
                    </motion.div>
                </div>
            </section>

            {/* Visual */}
            {item.image && (
                <section className="bg-ae-gray-100 pb-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-10">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, delay: 0.15 }}
                            className="relative rounded-3xl overflow-hidden shadow-ae-lift ring-1 ring-black/5"
                        >
                            <img
                                src={item.image}
                                alt={`${item.client} — ${item.title}`}
                                data-testid="work-detail-image"
                                className="w-full h-auto object-cover"
                            />
                        </motion.div>
                    </div>
                </section>
            )}

            {/* Body */}
            <section className="py-16 md:py-24">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
                    {/* Left – narrative */}
                    <div className="lg:col-span-7 space-y-12">
                        {item.summary && (
                            <div data-testid="work-detail-summary">
                                <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange">
                                    The Story
                                </div>
                                <p className="mt-4 text-lg md:text-xl text-ae-gray-700 leading-relaxed">
                                    {item.summary}
                                </p>
                            </div>
                        )}

                        {item.objective && (
                            <div data-testid="work-detail-objective">
                                <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange">
                                    Objective
                                </div>
                                <p className="mt-4 text-base md:text-lg text-ae-gray-700 leading-relaxed">
                                    {item.objective}
                                </p>
                            </div>
                        )}

                        {item.approach && item.approach.length > 0 && (
                            <div data-testid="work-detail-approach">
                                <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange">
                                    Approach
                                </div>
                                <ul className="mt-5 space-y-3">
                                    {item.approach.map((a, i) => (
                                        <li
                                            key={i}
                                            className="flex items-start gap-3"
                                        >
                                            <CheckCircle2 className="h-5 w-5 text-ae-orange shrink-0 mt-0.5" />
                                            <span className="text-base md:text-lg text-ae-gray-700">
                                                {a}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {!hasDetail && (
                            <div className="text-ae-gray-700 text-base md:text-lg">
                                Full case study available on request — drop us a line
                                and we&apos;ll walk you through the work.
                            </div>
                        )}
                    </div>

                    {/* Right – stats + meta */}
                    <aside className="lg:col-span-5 space-y-6">
                        {item.results && item.results.length > 0 && (
                            <div
                                data-testid="work-detail-results"
                                className="rounded-3xl bg-ae-ink text-white p-6 md:p-8"
                            >
                                <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange-light">
                                    Results
                                </div>
                                <div className="mt-6 grid grid-cols-1 gap-5">
                                    {item.results.map((r) => (
                                        <div
                                            key={r.label}
                                            className="border-b border-white/10 pb-5 last:border-0 last:pb-0"
                                        >
                                            <div className="font-display font-extrabold text-3xl md:text-4xl text-white tracking-tight">
                                                {r.value}
                                            </div>
                                            <div className="mt-1 text-sm text-white/65">
                                                {r.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {item.services && item.services.length > 0 && (
                            <div
                                data-testid="work-detail-services"
                                className="rounded-3xl bg-white border border-black/5 shadow-ae-soft p-6 md:p-8"
                            >
                                <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange">
                                    Services
                                </div>
                                <div className="mt-5 flex flex-wrap gap-2">
                                    {item.services.map((s) => (
                                        <span
                                            key={s}
                                            className="text-xs font-semibold uppercase tracking-wider text-ae-ink bg-ae-gray-100 px-3 py-2 rounded-full"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="rounded-3xl bg-gradient-to-br from-ae-orange to-ae-orange-deep text-white p-6 md:p-8">
                            <div className="text-xs uppercase tracking-[0.22em] font-bold text-white/80">
                                Have a brief like this?
                            </div>
                            <div className="mt-3 font-display font-extrabold text-2xl leading-tight">
                                Let&apos;s amplify your next campaign.
                            </div>
                            <Link
                                to="/#contact"
                                data-testid="work-detail-cta-contact"
                                className="mt-5 inline-flex items-center gap-2 bg-white text-ae-ink rounded-full px-5 py-3 text-sm font-semibold hover:bg-ae-ink hover:text-white transition-colors"
                            >
                                Start a Project <ArrowUpRight className="h-4 w-4" />
                            </Link>
                        </div>
                    </aside>
                </div>
            </section>

            {/* More work */}
            <section className="pb-24 md:pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="text-xs uppercase tracking-[0.22em] font-bold text-ae-orange inline-flex items-center gap-2">
                                <Sparkles className="h-4 w-4" /> More work
                            </div>
                            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-ae-ink">
                                Other projects we&apos;ve amplified.
                            </h2>
                        </div>
                        <Link
                            to="/#work"
                            className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-ae-ink hover:text-ae-orange"
                            data-testid="work-detail-view-all"
                        >
                            View all <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {FEATURED_WORK.filter((w) => w.slug !== item.slug)
                            .slice(0, 3)
                            .map((other) => (
                                <Link
                                    key={other.slug}
                                    to={`/work/${other.slug}`}
                                    data-testid={`work-detail-related-${other.slug}`}
                                    className="group relative rounded-3xl overflow-hidden aspect-[4/3] bg-ae-gray-100 hover:ring-2 hover:ring-ae-orange transition-all"
                                >
                                    {other.image ? (
                                        <img
                                            src={other.image}
                                            alt={other.title}
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    ) : (
                                        <div className="absolute inset-0 bg-gradient-to-br from-ae-ink to-ae-orange-deep" />
                                    )}
                                    <div className="absolute inset-0 bg-gradient-to-t from-ae-ink/85 via-ae-ink/30 to-transparent" />
                                    <div className="absolute inset-x-0 bottom-0 p-5">
                                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/75">
                                            {other.category}
                                        </div>
                                        <div className="mt-1 font-display font-bold text-lg text-white">
                                            {other.title}
                                        </div>
                                        <div className="text-sm text-white/75">
                                            {other.client}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                    </div>
                </div>
            </section>
        </main>
    );
}
